import type { Comic } from '@tanko/shared'
import { isBackedUp, isRegistered } from './comic-sync'

/**
 * 從書庫移除作品時的出口。
 *
 * 判斷的是「雲端有沒有這一本的東西」，也就是 isRegistered，而不是「有沒有備份完成」。
 * pending 的作品在雲端已經有目錄項——那正是已註冊未備份的意思——只刪本地的話，那筆
 * 目錄項會永遠留在雲端清單上，而本地再也沒有東西指向它。
 *
 * 預設仍是只從書庫移除：本地移除的語意一直是「從書庫移除，硬碟檔案不動」，而雲端那份
 * 可能是唯一的備份。但也不能不問，否則孤兒只會持續累積，而使用者沒有任何介面看得出
 * 雲端清單裡哪一筆在本地已經沒有對應。
 */
export type RemovalScope = 'library-only' | 'library-and-cloud'

export interface RemovalChoice {
  scope: RemovalScope
  label: string
  /** 預設出口。確認對話的主要按鈕用它。 */
  primary: boolean
}

export interface RemovalPlan {
  /** 可選的出口，第一筆是預設。只有一筆時呈現一般的單一確認即可。 */
  choices: RemovalChoice[]
  description: string
}

const LIBRARY_ONLY: RemovalChoice = { scope: 'library-only', label: '只從書庫移除', primary: true }
const LIBRARY_AND_CLOUD: RemovalChoice = { scope: 'library-and-cloud', label: '連雲端一起刪除', primary: false }

export function removalPlan(comic: Pick<Comic, 'syncState'>): RemovalPlan {
  if (!isRegistered(comic)) {
    return {
      choices: [{ ...LIBRARY_ONLY, label: '移除作品' }],
      description: '將移除這本作品的書庫資料與分類指派。這本還沒有加入雲端，電腦裡的原始檔案也不會刪除。',
    }
  }
  // 已備份的雲端那份含圖片與閱讀狀態；只註冊過的只有目錄項，講清楚差別才知道刪掉的是什麼。
  const cloudSide = isBackedUp(comic)
    ? '這本在私人雲端有一份備份。只從書庫移除的話，雲端那份會留著但不再與本地關聯；連雲端一起刪則會刪掉雲端的圖片、閱讀進度與書籤。'
    : '這本已經加入雲端目錄但還沒備份。只從書庫移除的話，雲端清單上會留下一筆沒有本地對應的項目；連雲端一起刪則會把那筆目錄項一併移除。'
  return {
    choices: [LIBRARY_ONLY, LIBRARY_AND_CLOUD],
    description: `${cloudSide}電腦裡的原始檔案兩種都不會刪除。`,
  }
}
